import type { components } from '@connected/api-client/api.generated';
import type { ColumnDef } from '@tanstack/react-table';

import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import { Carousel, CarouselContent, CarouselIndicators, CarouselItem } from '@connected/ui/components/carousel';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@connected/ui/components/dropdown-menu';
import { Image } from '@connected/ui/components/image';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useSearch } from '@tanstack/react-router';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckmarkCircle } from '@/components/checkmark-circle';
import { ComingSoonModal } from '@/components/coming-soon-modal';
import { DataTable } from '@/components/data-table';
import { LoadingPage } from '@/components/loading-page';
import { Observation } from '@/components/observation';
import {
    ScrollableTable,
    ScrollableTableBody,
    ScrollableTableCell,
    ScrollableTableHead,
    ScrollableTableHeader,
    ScrollableTableRow,
} from '@/components/scrollable-table';
import SearchInput from '@/components/search-input';

export const Route = createFileRoute(
    '/_public/customer/locations/$locationId/electrical/projects/$projectId/_overview/distribution-boards/',
)({
    component: RouteComponent,
});

export type DistributionBoardProps = components['schemas']['ListElectricalProjectDistributionBoardPortalQueryResultBoard'];

function RouteComponent() {
    const { t } = useTranslation('customer-module-electrical');
    const params = Route.useParams();
    const [comingSoonModalOpen, setComingSoonModalOpen] = useState<boolean>(false);

    const { search } = useSearch({ strict: false });

    const distributionBoardsQuery = useQuery(portaalQueryOptions.electrical.distributionBoards.list({
        ElectricalProjectId: params.projectId ?? null,
        Kinds: [],
    }));

    const observationQuery = useQuery(portaalQueryOptions.electrical.observations.list({
        ElectricalProjectId: params.projectId ?? null,
        ObjectKind: 'DistributionBoard',
    }));

    const columns = useMemo<ColumnDef<DistributionBoardProps>[]>(() => [
        {
            accessorKey: 'Identifier',
            meta: {
                sticky: true,
                width: 200,
            },
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.list.name')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell variant="link">
                    <Link
                        to="/customer/locations/$locationId/electrical/projects/$projectId/distribution-boards/$distributionId"
                        params={{
                            locationId: params.locationId,
                            projectId: params.projectId,
                            distributionId: row?.original?.ElectricalProjectDistributionBoardId ?? '',
                        }}
                    >
                        {row.original?.Identifier}
                    </Link>
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'SevereObservationCount',
            header: ({ column }) => (
                <DataTable.Header
                    variant="sortDesc"
                    column={column}
                    align="center"
                >
                    {t('module-electrical.distribution-boards.list.severe')}
                </DataTable.Header>
            ),
            cell: ({ row }) => {
                const board = row.original;

                if (!board || (board.SevereObservationCount ?? 0) === 0)
                    return null;

                if ((board.SevereObservationCount ?? 0) === 1) {
                    return (
                        <Link
                            to="/customer/locations/$locationId/electrical/projects/$projectId/distribution-boards/$distributionId/observations/$observationId"
                            params={{
                                locationId: params.locationId,
                                projectId: params.projectId,
                                distributionId: board.ElectricalProjectDistributionBoardId!,
                                observationId: board.SevereElectricalProjectObservationId!,
                            }}
                        >
                            <DataTable.Cell
                                variant="observation"
                                props={{
                                    label: distributionBoardsQuery?.data?.Codes?.find(code => code.Kind === 'Severe')?.Kind ?? t('module-electrical.distribution-boards.list.severe'),
                                    color: distributionBoardsQuery?.data?.Codes?.find(code => code.Kind === 'Severe')?.HexColorCode ?? 'default',
                                    children: board.SevereObservationCount ?? 0,
                                }}
                                label={t('module-electrical.distribution-boards.list.severe')}
                            />
                        </Link>
                    );
                }

                return (
                    <DataTable.Cell
                        variant="observation"
                        props={{
                            label: distributionBoardsQuery?.data?.Codes?.find(code => code.Kind === 'Severe')?.Kind ?? t('module-electrical.distribution-boards.list.severe'),
                            color: distributionBoardsQuery?.data?.Codes?.find(code => code.Kind === 'Severe')?.HexColorCode ?? 'default',
                            children: board.SevereObservationCount ?? 0,
                        }}
                        label={t('module-electrical.distribution-boards.list.severe')}
                    />
                );
            },
            sortingFn: (rowA, rowB) => {
                const countA = rowA.original.SevereObservationCount ?? 0;
                const countB = rowB.original.SevereObservationCount ?? 0;
                return countB - countA;
            },
        },
        {
            accessorKey: 'SeriousObservationCount',
            header: ({ column }) => (
                <DataTable.Header
                    variant="sortDesc"
                    column={column}
                    align="center"
                >
                    {t('module-electrical.distribution-boards.list.serious')}
                </DataTable.Header>
            ),
            cell: ({ row }) => {
                const board = row.original;

                if (!board || (board.SeriousObservationCount ?? 0) === 0)
                    return null;

                if ((board.SeriousObservationCount ?? 0) === 1) {
                    return (
                        <Link
                            to="/customer/locations/$locationId/electrical/projects/$projectId/distribution-boards/$distributionId/observations/$observationId"
                            params={{
                                locationId: params.locationId,
                                projectId: params.projectId,
                                distributionId: board.ElectricalProjectDistributionBoardId!,
                                observationId: board.SeriousElectricalProjectObservationId!,
                            }}
                        >
                            <DataTable.Cell
                                variant="observation"
                                props={{
                                    label: distributionBoardsQuery?.data?.Codes?.find(code => code.Kind === 'Serious')?.Kind ?? t('module-electrical.distribution-boards.list.serious'),
                                    color: distributionBoardsQuery?.data?.Codes?.find(code => code.Kind === 'Serious')?.HexColorCode ?? '#e9eaeb',
                                    children: board.SeriousObservationCount ?? 0,
                                }}
                                label={t('module-electrical.distribution-boards.list.serious')}
                            />
                        </Link>
                    );
                }

                return (
                    <DataTable.Cell
                        variant="observation"
                        props={{
                            label: distributionBoardsQuery?.data?.Codes?.find(code => code.Kind === 'Serious')?.Kind ?? t('module-electrical.distribution-boards.list.serious'),
                            color: distributionBoardsQuery?.data?.Codes?.find(code => code.Kind === 'Serious')?.HexColorCode ?? '#e9eaeb',
                            children: board.SeriousObservationCount ?? 0,
                        }}
                        label={t('module-electrical.distribution-boards.list.serious')}
                    />
                );
            },
            sortingFn: (rowA, rowB) => {
                const countA = rowA.original.SeriousObservationCount ?? 0;
                const countB = rowB.original.SeriousObservationCount ?? 0;
                return countB - countA;
            },
        },
        {
            accessorKey: 'MinorObservationCount',
            header: ({ column }) => (
                <DataTable.Header
                    variant="sortDesc"
                    column={column}
                    align="center"
                >
                    {t('module-electrical.distribution-boards.list.minor')}
                </DataTable.Header>
            ),
            cell: ({ row }) => {
                const board = row.original;

                if (!board || (board.MinorObservationCount ?? 0) === 0)
                    return null;

                if ((board.MinorObservationCount ?? 0) === 1) {
                    return (
                        <Link
                            to="/customer/locations/$locationId/electrical/projects/$projectId/distribution-boards/$distributionId/observations/$observationId"
                            params={{
                                locationId: params.locationId,
                                projectId: params.projectId,
                                distributionId: board.ElectricalProjectDistributionBoardId!,
                                observationId: board.MinorElectricalProjectObservationId!,
                            }}
                        >
                            <DataTable.Cell
                                variant="observation"
                                props={{
                                    label: distributionBoardsQuery?.data?.Codes?.find(code => code.Kind === 'Minor')?.Kind ?? t('module-electrical.distribution-boards.list.minor'),
                                    color: distributionBoardsQuery?.data?.Codes?.find(code => code.Kind === 'Minor')?.HexColorCode ?? 'default',
                                    children: board.MinorObservationCount ?? 0,
                                }}
                                label={t('module-electrical.distribution-boards.list.minor')}
                            />
                        </Link>
                    );
                }

                return (
                    <DataTable.Cell
                        variant="observation"
                        props={{
                            label: distributionBoardsQuery?.data?.Codes?.find(code => code.Kind === 'Minor')?.Kind ?? t('module-electrical.distribution-boards.list.minor'),
                            color: distributionBoardsQuery?.data?.Codes?.find(code => code.Kind === 'Minor')?.HexColorCode ?? 'default',
                            children: board.MinorObservationCount ?? 0,
                        }}
                        label={t('module-electrical.distribution-boards.list.minor')}
                    />
                );
            },
            sortingFn: (rowA, rowB) => {
                const countA = rowA.original.MinorObservationCount ?? 0;
                const countB = rowB.original.MinorObservationCount ?? 0;
                return countB - countA;
            },
        },
        {
            accessorKey: 'RemarkObservationCount',
            header: ({ column }) => (
                <DataTable.Header
                    variant="sortDesc"
                    column={column}
                    align="center"
                >
                    {t('module-electrical.distribution-boards.list.remarks')}
                </DataTable.Header>
            ),
            cell: ({ row }) => {
                const board = row.original;

                if (!board || (board.RemarkObservationCount ?? 0) === 0)
                    return null;

                if ((board.RemarkObservationCount ?? 0) === 1) {
                    return (
                        <Link
                            to="/customer/locations/$locationId/electrical/projects/$projectId/distribution-boards/$distributionId/observations/$observationId"
                            params={{
                                locationId: params.locationId,
                                projectId: params.projectId,
                                distributionId: board.ElectricalProjectDistributionBoardId!,
                                observationId: board.RemarkElectricalProjectObservationId!,
                            }}
                        >
                            <DataTable.Cell
                                variant="observation"
                                props={{
                                    label: distributionBoardsQuery?.data?.Codes?.find(code => code.Kind === 'Remark')?.Kind ?? t('module-electrical.distribution-boards.list.remarks'),
                                    color: distributionBoardsQuery?.data?.Codes?.find(code => code.Kind === 'Remark')?.HexColorCode ?? 'default',
                                    children: board.RemarkObservationCount ?? 0,
                                }}
                                label={t('module-electrical.distribution-boards.list.remarks')}
                            />
                        </Link>
                    );
                }

                return (
                    <DataTable.Cell
                        variant="observation"
                        props={{
                            label: distributionBoardsQuery?.data?.Codes?.find(code => code.Kind === 'Remark')?.Kind ?? t('module-electrical.distribution-boards.list.remarks'),
                            color: distributionBoardsQuery?.data?.Codes?.find(code => code.Kind === 'Remark')?.HexColorCode ?? 'default',
                            children: board.RemarkObservationCount ?? 0,
                        }}
                        label={t('module-electrical.distribution-boards.list.remarks')}
                    />
                );
            },
            sortingFn: (rowA, rowB) => {
                const countA = rowA.original.RemarkObservationCount ?? 0;
                const countB = rowB.original.RemarkObservationCount ?? 0;
                return countB - countA;
            },
        },
    ], [t, distributionBoardsQuery?.data?.Codes, params.locationId, params.projectId]);

    if (distributionBoardsQuery.isLoading || observationQuery.isLoading) {
        return <LoadingPage />;
    }

    if (!distributionBoardsQuery?.data) {
        return (
            <div className="flex items-center justify-center h-full">
                <p>{t('module-electrical.distribution-boards.no-data')}</p>
            </div>
        );
    }

    // Apply 'Naamloos' logic to DistributionBoards
    const distributionBoards = (distributionBoardsQuery?.data?.DistributionBoards ?? []).map((board, idx) => ({
        ...board,
        Identifier: board.Identifier ? board.Identifier : `Naamloos ${idx + 1}`,
    }));

    const filteredBoards = distributionBoards.filter(search ? x => x.Identifier?.toLocaleLowerCase().includes(search.toLowerCase()) : x => x);

    return (
        <>
            <div className="flex flex-col lg:flex-row gap-4 lg:gap-9">
                <div className="w-full lg:w-1/2">
                    <DataTable.TableHead
                        title={t('module-electrical.distribution-boards.title')}
                        label={`${distributionBoards.length ?? 0} ${t('module-electrical.distribution-boards.title')}`}
                        subtitle={t('module-electrical.distribution-boards.subtitle')}
                    />
                    <div className="flex flex-col sm:flex-row gap-2 border-x border-t justify-start p-3">
                        <SearchInput route={Route} />
                    </div>
                    <DataTable
                        className="rounded-none"
                        whiteBgHeader={true}
                        columns={columns}
                        data={filteredBoards}
                    />
                </div>
                <div className="w-full lg:w-1/2 flex flex-col gap-4">
                    {filteredBoards.map(data => (
                        <div
                            key={data.ElectricalProjectDistributionBoardId}
                            className="border rounded-2xl p-4 md:p-5"
                            style={{ cursor: 'pointer' }}
                        >
                            <Link
                                to="/customer/locations/$locationId/electrical/projects/$projectId/distribution-boards/$distributionId"
                                params={{
                                    locationId: params.locationId,
                                    projectId: params.projectId,
                                    distributionId: data.ElectricalProjectDistributionBoardId!,
                                }}
                            >
                                <div className="flex justify-between gap-4">
                                    <h2 className="flex text-lg md:text-xl font-semibold">
                                        {data.Identifier ?? 'Onbekend'}
                                    </h2>
                                    <div className="flex flex-row gap-2">
                                        <p className="hidden sm:block text-sm">
                                            {t('module-electrical.distribution-boards.more')}
                                        </p>
                                        <ChevronRight size={20} />
                                    </div>
                                </div>
                                <p className="text-sm text-text-primary/60">
                                    {t('module-electrical.distribution-boards.floor')}
                                    {' '}
                                    {data.FloorNumber ?? 'Locatie onbekend'}
                                    ,
                                    {' '}
                                    {t('module-electrical.distribution-boards.room')}
                                    {' '}
                                    {data.SpaceNumber ?? 'Locatie onbekend'}
                                </p>
                            </Link>
                            <div className="flex flex-col md:flex-row lg:flex-row gap-4 pt-4">
                                <div className="w-full md:w-1/3 lg:w-1/3 h-full flex flex-col gap-4">
                                    <Carousel loop>
                                        <CarouselContent className="-ml-2 md:-ml-4">
                                            {data.Files?.slice(0, 5).map((file, idx) => (
                                                <CarouselItem className="pl-2 md:pl-4" key={file.ElectricalProjectFileId ?? idx}>
                                                    {file.File && (
                                                        <Image
                                                            previewer
                                                            className="h-52 w-auto object-contain rounded-lg"
                                                            alt={file?.File?.Name ?? ''}
                                                            queryOptions={portaalQueryOptions.files.electricalProject({
                                                                ElectricalProjectFileId: file.ElectricalProjectFileId!,
                                                                ImageQuality: 'OriginalWebp',
                                                            })}
                                                            enabled={!!file.ElectricalProjectFileId}
                                                        />
                                                    )}
                                                </CarouselItem>
                                            ))}
                                            {data.Files?.length === 0 && (
                                                <CarouselItem>
                                                    <div className="flex items-center justify-center h-52 text-text-primary/50 rounded-lg border-2 border-dashed">
                                                        <p className="text-center">{t('module-electrical.pv-installations.messages.noImagesFound')}</p>
                                                    </div>
                                                </CarouselItem>
                                            )}
                                        </CarouselContent>
                                        <CarouselIndicators className="pb-0" />
                                    </Carousel>
                                    <div className="flex flex-col sm:flex-row justify-between gap-2 w-full">
                                        <Button
                                            variant="link"
                                            className="flex items-center gap-1 border px-5 rounded-lg h-10 w-full sm:w-1/2 text-foreground hover:no-underline hover:bg-background justify-center"
                                            onClick={() => setComingSoonModalOpen(true)}
                                        >
                                            <span className="text-sm">{t('module-electrical.distribution-boards.buttons.notification')}</span>
                                        </Button>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger className="flex items-center gap-2 border px-3 rounded-lg h-10 w-full sm:w-1/2 text-foreground hover:no-underline hover:bg-background justify-center">
                                                <span className="text-sm">{t('module-electrical.distribution-boards.buttons.request')}</span>
                                                <ChevronDown className="w-5 h-5" />
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent className="flex flex-col" align="start">
                                                <DropdownMenuItem
                                                    className="p-2 focus:bg-background-secondary focus:text-popover-foreground cursor-pointer"
                                                    onClick={() => setComingSoonModalOpen(true)}
                                                >
                                                    Onderhoud
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    className="p-2 focus:bg-background-secondary focus:text-popover-foreground cursor-pointer"
                                                    onClick={() => setComingSoonModalOpen(true)}
                                                >
                                                    Power quality
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    className="p-2 focus:bg-background-secondary focus:text-popover-foreground cursor-pointer"
                                                    onClick={() => setComingSoonModalOpen(true)}
                                                >
                                                    Offerte vervanging
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    className="p-2 focus:bg-background-secondary focus:text-popover-foreground cursor-pointer"
                                                    onClick={() => setComingSoonModalOpen(true)}
                                                >
                                                    Thermografie
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                        <ComingSoonModal open={comingSoonModalOpen} onOpenChange={setComingSoonModalOpen} />
                                    </div>
                                </div>
                                <div className="flex w-full justify-center rounded-tl-2xl rounded-tr-2xl overflow-hidden">
                                    <ScrollableTable maxHeight={15.3} className="rounded-none">
                                        <ScrollableTableHeader>
                                            <ScrollableTableRow className="h-12">
                                                <ScrollableTableHead className="text-text-primary/70 bg-background-secondary px-4">
                                                    {t(
                                                        'module-electrical.distribution-boards.distribution-board.notifications-table.header.name',
                                                    )}
                                                </ScrollableTableHead>
                                                <ScrollableTableHead className="text-text-primary/70 bg-background-secondary px-4 flex items-center justify-center">
                                                    {t(
                                                        'module-electrical.distribution-boards.distribution-board.notifications-table.header.status',
                                                    )}
                                                </ScrollableTableHead>
                                                <ScrollableTableHead className="text-text-primary/70 bg-background-secondary px-4">
                                                    {t(
                                                        'module-electrical.distribution-boards.distribution-board.notifications-table.header.date',
                                                    )}
                                                </ScrollableTableHead>
                                            </ScrollableTableRow>
                                        </ScrollableTableHeader>
                                        <ScrollableTableBody>
                                            {(() => {
                                                const boardObservations = observationQuery?.data?.Observations?.filter(obs => obs.ElectricalProjectDistributionBoardId === data.ElectricalProjectDistributionBoardId) ?? [];
                                                if (boardObservations.length === 0) {
                                                    return (
                                                        <div className="flex items-center justify-center h-12 text-text-primary/50 rounded-lg border-2 border-dashed">
                                                            <p>{t('module-electrical.pv-installations.messages.noObservationsFound')}</p>
                                                        </div>
                                                    );
                                                }
                                                return boardObservations.map(observation => (
                                                    <ScrollableTableRow
                                                        key={observation.ElectricalProjectObservationId}
                                                        className="cursor-pointer hover:bg-background-secondary h-12"
                                                    >
                                                        <ScrollableTableCell className="px-4">
                                                            <div className="flex items-center gap-2">
                                                                <Observation
                                                                    color={observation?.HexColorCode ?? '#FFFFFF'}
                                                                    label={t('module-electrical.report.observations.details-table.columns.observation')}
                                                                    children={[]}
                                                                    className="scale-75"
                                                                />
                                                                <Link
                                                                    to="/customer/locations/$locationId/electrical/projects/$projectId/distribution-boards/$distributionId/observations/$observationId"
                                                                    params={{
                                                                        locationId: params.locationId,
                                                                        projectId: params.projectId,
                                                                        distributionId: observation.ElectricalProjectDistributionBoardId!,
                                                                        observationId: observation.ElectricalProjectObservationId!,
                                                                    }}
                                                                    className="font-bold text-primary cursor-pointer"
                                                                >
                                                                    {t('module-electrical.report.observations.details-table.columns.observation')}
                                                                </Link>
                                                            </div>
                                                        </ScrollableTableCell>
                                                        <ScrollableTableCell className="px-4 flex items-center justify-center">
                                                            <CheckmarkCircle checked={observation.IsFixed} />
                                                        </ScrollableTableCell>
                                                        <ScrollableTableCell className="px-4">
                                                            {observation.Date ? new Date(observation.Date).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}
                                                        </ScrollableTableCell>
                                                    </ScrollableTableRow>
                                                ));
                                            })()}
                                        </ScrollableTableBody>
                                    </ScrollableTable>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
}
